import type { SyncStatus } from '@orgus/protocol';
import { CircleAlert, CircleCheck, CircleDashed, RefreshCw, TriangleAlert } from '@orgus/ui/icons';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, ErrorState, GroupChevron, RelativeTime, formatClock, formatDate, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { FactGrid } from '../detail/FactGrid.tsx';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { dayRangeText } from './model.ts';
import { syncNow } from './useFinance.ts';

const NAME = { billing: 'Anthropic billing', github: 'GitHub' } as const;

/** Ends a provider's message with a full stop, so 'why' and 'next' never run together. */
const sentence = (s: string) => {
  const t = s.trim();
  return !t || /[.!?]$/.test(t) ? t : `${t}.`;
};

/**
 * One provider pull: its state as icon plus label and when it last pulled, at rest; what it
 * covers, the next pull and the key behind Details; and Pull now.
 */
export function SyncRow({ status }: { status: SyncStatus }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const narrow = useMediaQuery(NARROW_QUERY);
  const pull = async () => {
    setBusy(true);
    setError(null);
    try {
      await syncNow(status.kind);
    } catch (e) {
      setError(toUiError(e, { what: `${NAME[status.kind]} was not pulled.` }));
    } finally {
      setBusy(false);
    }
  };
  const running = busy || status.running;
  const badge = !status.configured ? (
    <Badge tone="idle" icon={<CircleDashed />}>
      Not set up
    </Badge>
  ) : running ? (
    <Badge tone="working" icon={<RefreshCw className="animate-spin" />}>
      Pulling
    </Badge>
  ) : status.error && status.partial ? (
    <Badge tone="waiting" icon={<TriangleAlert />}>
      Pulled with gaps
    </Badge>
  ) : status.error ? (
    <Badge tone="failed" icon={<CircleAlert />}>
      Last pull failed
    </Badge>
  ) : status.lastOkAt ? (
    <Badge tone="passed" icon={<CircleCheck />}>
      Up to date
    </Badge>
  ) : (
    <Badge tone="queued" icon={<CircleDashed />}>
      Not pulled yet
    </Badge>
  );
  const facts = [
    { label: 'Covers', value: status.coversFrom && status.coversTo ? `${dayRangeText(status.coversFrom, status.coversTo)}${status.kind === 'billing' ? ', UTC days' : ''}` : null },
    { label: 'Next pull', value: status.nextRunAt ? `${formatDate(status.nextRunAt)}, ${formatClock(status.nextRunAt)}` : null },
    { label: status.kind === 'billing' ? 'Key' : 'Token', value: status.credentialFrom ? `${status.credentialFrom === 'settings' ? 'Settings' : status.credentialFrom}, ending ${status.hint ?? ''}`.trim() : null },
  ];
  const hasFacts = facts.some((f) => f.value);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface px-4 py-3 hairline">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* On a phone the name and state take the first line; when it pulled and the actions the second. */}
        <span className="flex min-w-0 items-center gap-3 max-md:w-full">
          <span className="truncate text-body font-medium text-fg">{NAME[status.kind]}</span>
          <span className="shrink-0">{badge}</span>
        </span>
        <span className="min-w-0 flex-1 truncate text-ui text-fg-muted">
          {!status.configured ? (
            <>
              {status.kind === 'billing' ? 'Needs an Admin API key' : 'Needs a token and an owner'}{' '}
              <Link to="/settings/finance" className="font-medium text-fg underline-offset-2 hover:underline">
                Set it up
              </Link>
            </>
          ) : status.lastOkAt ? (
            <>
              Pulled <RelativeTime ts={status.lastOkAt} />
            </>
          ) : (
            'Never pulled'
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {status.configured && hasFacts && (
            <Button size="sm" variant="ghost" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen((o) => !o)}>
              <GroupChevron open={open} />
              Details
            </Button>
          )}
          {status.configured && (
            <Button size="sm" variant="secondary" icon={<RefreshCw />} loading={running} onClick={() => void pull()}>
              <span className="max-md:sr-only">Pull now</span>
            </Button>
          )}
        </span>
      </div>
      {open && hasFacts && (
        <div id={detailsId} className="border-t border-border-subtle pt-3">
          {/* On a phone each fact takes the full width on its own line, so a range or a key hint wraps rather than cuts. */}
          {narrow ? (
            <dl className="flex flex-col gap-3" aria-label={`${NAME[status.kind]} pull`}>
              {facts
                .filter((f) => f.value)
                .map((f) => (
                  <div key={f.label} className="flex flex-col gap-1">
                    <dt className="text-caption text-fg-subtle">{f.label}</dt>
                    <dd className="break-words text-ui text-fg-muted num">{f.value}</dd>
                  </div>
                ))}
            </dl>
          ) : (
            <FactGrid lines={[facts]} label={`${NAME[status.kind]} pull`} />
          )}
        </div>
      )}
      {status.error && !running && (
        <div className="flex items-start gap-2 text-ui text-fg">
          {status.partial ? <TriangleAlert className="mt-0.5 size-4 shrink-0 text-waiting" aria-hidden /> : <CircleAlert className="mt-0.5 size-4 shrink-0 text-failed" aria-hidden />}
          <div className="flex flex-col gap-0.5">
            <p>
              <span className="font-medium">{sentence(status.error.what)}</span> {sentence(status.error.why)}
            </p>
            <p className="text-fg-muted">{sentence(status.error.next)}</p>
          </div>
        </div>
      )}
      {error && <ErrorState error={error} variant="inline" />}
    </div>
  );
}
